/**
 * KeybookGate — feature toggle gate.
 *
 * Post-SSE refactor: enabling keybook is a single click. No passkey
 * registration, no key derivation, no Manager team bootstrap. Server
 * holds the master key; per-tenant keys are derived from tenantId
 * automatically.
 */
import { useEffect, useRef, useState, useCallback } from 'react'
import { useTranslation } from '@papan/i18n'
import { useToast, getThemeClass, useTheme } from '@papan/core-ui/ui'
import { Loader2, BookKey, ShieldCheck } from 'lucide-react'
import { keybookApi, type FeatureStatus } from '../client/api.js'

interface KeybookGateProps {
  tenantId: string
  isOrg: boolean
  /** Caller's role in the tenant — owners can enable; others wait. */
  userRole?: string
  children: React.ReactNode
}

export function KeybookGate({ tenantId, isOrg, userRole, children }: KeybookGateProps) {
  const { t } = useTranslation('keybook')
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const toast = useToast()
  // ToastContext value is rebuilt on every ToastProvider render (any
  // add/remove). Reading toast through a ref keeps callbacks stable so
  // the reload effect doesn't refire — which used to unmount children
  // mid-flow and reset their dialog state.
  const toastRef = useRef(toast)
  toastRef.current = toast

  const [status, setStatus] = useState<FeatureStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)

  const reload = useCallback(async () => {
    if (!tenantId) return
    setLoading(true)
    try {
      const s = await keybookApi.featureStatus(tenantId)
      setStatus(s)
    } catch (err: any) {
      toastRef.current.error(err?.message || t('gateLoadFailedToast'))
    } finally {
      setLoading(false)
    }
  }, [tenantId])

  useEffect(() => { void reload() }, [reload])

  const enable = useCallback(async () => {
    if (busy) return
    setBusy(true)
    try {
      await keybookApi.enableKeybook(tenantId)
      toastRef.current.success(t('gateEnabledToast'))
      await reload()
    } catch (err: any) {
      toastRef.current.error(err?.message || t('gateEnableFailedToast'))
    } finally {
      setBusy(false)
    }
  }, [busy, tenantId, reload])

  // Only show the loader on the *initial* load. Once we already have a
  // status, subsequent reloads (e.g., right after enable) keep the
  // children mounted so their dialog/form state survives.
  if (loading && !status) {
    return (
      <div className={`${themeClass} themed-bg-primary flex-1 flex items-center justify-center`}>
        <div className="flex items-center gap-2 themed-text-muted">
          <Loader2 size={16} className="animate-spin" />
          {t('gateLoading')}
        </div>
      </div>
    )
  }

  if (status?.enabled) {
    return <>{children}</>
  }

  // Org-non-owner: must wait for an owner to enable.
  if (isOrg && userRole !== 'owner') {
    return (
      <div className={`${themeClass} themed-bg-primary flex-1 flex items-center justify-center`}>
        <div className="max-w-md text-center space-y-3">
          <BookKey size={32} className="themed-icon mx-auto" />
          <h2 className="themed-text text-lg font-medium">{t('gateOtherUserOrgWaitTitle')}</h2>
          <p className="themed-text-muted text-sm">{t('gateOtherUserOrgWaitBody')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className={`${themeClass} themed-bg-primary flex-1 flex items-center justify-center p-6`}>
      <div className="max-w-md space-y-5">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-md themed-bg-tertiary">
            <BookKey size={20} className="text-emerald-500" />
          </div>
          <h2 className="text-lg font-medium themed-text">
            {t(isOrg ? 'gateHeadingOrg' : 'gateHeadingPersonal')}
          </h2>
        </div>
        <p className="text-sm themed-text-secondary">
          {t(isOrg ? 'gateBodyOrg' : 'gateBodyPersonal')}
        </p>
        {isOrg && (
          <p className="text-xs themed-text-muted">{t('gateBodyOrgScopeNote')}</p>
        )}
        <button
          onClick={enable}
          disabled={busy}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 themed-btn-primary rounded-md disabled:opacity-50"
        >
          {busy && <Loader2 size={14} className="animate-spin" />}
          <ShieldCheck size={16} />
          {t('gateEnableButton')}
        </button>
      </div>
    </div>
  )
}
